import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Box, Typography, Alert, Autocomplete, Chip, Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import {
  createDripEntry, updateDripEntry, previewDripEntry, sendDripTest, dripErrorMessage,
  type DripEntry, type LeadStatus, type DripPreview, type DripSettings,
} from '../services/drip-api';
import { getCurrentUser } from '../auth/LoginButton';

/**
 * Create or edit one drip entry.
 *
 * The form cascades: the action type decides what is asked next, so nobody fills in fields that do
 * not apply to their choice.
 *
 * Statuses on the "never email" list are NOT offered here, and the server refuses them by name even
 * if a request arrives with one. That belt-and-braces is deliberate: a DNC lead must not receive
 * marketing email because someone mis-clicked.
 */

const EMPTY: Partial<DripEntry> = {
  name: '', actionType: 'interval', subject: '', preheader: '', body: '',
  inactiveDays: 2, statuses: [], triggerEvent: 'lead_received', fromStatus: null, toStatus: null,
  sendGridTemplateId: '',
};

export default function DripEntryDialog({
  open, entry, leadStatuses, settings, onClose, onSaved,
}: {
  open: boolean;
  /** null = creating a new entry */
  entry: DripEntry | null;
  leadStatuses: LeadStatus[];
  /** App-wide defaults, so the form can show what a blank template box inherits. */
  settings: DripSettings | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [form, setForm] = useState<Partial<DripEntry>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DripPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // The preview opens as its own window over the form. In the form's own column it was a letterbox
  // barely three lines tall, which is no way to check an email.
  const [previewOpen, setPreviewOpen] = useState(false);
  // Prefilled with the signed-in user, because the only sensible first test is to yourself.
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(entry ? { ...entry } : { ...EMPTY });
      setError(null);
      setPreview(null);
      setPreviewOpen(false);
      setTestTo((getCurrentUser()?.email as string) || '');
      setTestResult(null);
    }
  }, [open, entry]);

  // Never-email statuses are not selectable anywhere in this form.
  const selectable = useMemo(
    () => leadStatuses.filter((s) => s.drip !== 'never')
      .sort((a, b) => (a.group + a.status).localeCompare(b.group + b.status)),
    [leadStatuses],
  );
  const neverCount = leadStatuses.length - selectable.length;

  const set = (k: keyof DripEntry, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only the fields belonging to the chosen action type are sent; the server ignores the rest,
      // but sending them would leave stale values on an entry that switched type.
      const payload: Partial<DripEntry> = {
        name: form.name, actionType: form.actionType, subject: form.subject,
        preheader: form.preheader || null, body: form.body,
        sendGridTemplateId: form.sendGridTemplateId || null,
        status: 'draft',
      };
      if (form.actionType === 'interval') {
        payload.inactiveDays = Number(form.inactiveDays);
        payload.statuses = form.statuses ?? [];
      } else {
        payload.triggerEvent = form.triggerEvent;
        if (form.triggerEvent === 'lead_status_changed') {
          payload.toStatus = form.toStatus ?? '';
          payload.fromStatus = form.fromStatus || null;
        }
      }
      if (entry) await updateDripEntry(entry.id, payload);
      else await createDripEntry(payload);
      onSaved(entry ? 'Entry updated.' : 'Entry saved as a draft.');
    } catch (err) {
      setError(dripErrorMessage(err, 'The entry could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{entry ? 'Edit drip entry' : 'New drip entry'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            label="Name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)}
            helperText="Only staff see this. For example: EAB Day 2 - what are you hoping to change"
            inputProps={{ maxLength: 120 }} fullWidth autoFocus
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>When should this send?</Typography>
            <ToggleButtonGroup
              exclusive value={form.actionType}
              onChange={(_e, v) => v && set('actionType', v)}
            >
              <ToggleButton value="trigger" sx={{ gap: 1 }}>
                <BoltIcon fontSize="small" /> Trigger
              </ToggleButton>
              <ToggleButton value="interval" sx={{ gap: 1 }}>
                <ScheduleIcon fontSize="small" /> Interval
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ mt: 1, color: 'var(--text-secondary)' }}>
              {form.actionType === 'trigger'
                ? 'Sends when something happens to a lead.'
                : 'Sends after a number of days without a reply, to leads in the statuses you choose.'}
            </Typography>
          </Box>

          <Divider />

          {form.actionType === 'interval' ? (
            <>
              <TextField
                label="Days of inactivity" type="number" value={form.inactiveDays ?? 0}
                onChange={(e) => set('inactiveDays', e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0, max: 365 }}
                helperText="Counted from the lead arriving. The approved cadence uses 2, 4, 7, 10, 12 and 14, then 30, 50, 70 and 90."
                sx={{ maxWidth: 320 }}
              />
              <Autocomplete
                multiple options={selectable} groupBy={(o) => o.group}
                getOptionLabel={(o) => o.status}
                value={selectable.filter((s) => (form.statuses ?? []).includes(s.status))}
                onChange={(_e, v) => set('statuses', v.map((s) => s.status))}
                isOptionEqualToValue={(o, v) => o.status === v.status}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip size="small" label={option.status} {...getTagProps({ index })} key={option.status} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params} label="Lead statuses"
                    helperText={`Grouped as Verity groups them. "Contact Not Yet Achieved" is the usual audience. ${neverCount} statuses (DNC, Bad, Not Qualified and the rest of the Rejected group) can never be emailed and are not listed.`}
                  />
                )}
              />
            </>
          ) : (
            <>
              <TextField
                select label="Trigger" value={form.triggerEvent ?? 'lead_received'}
                onChange={(e) => set('triggerEvent', e.target.value)}
                sx={{ maxWidth: 420 }}
                helperText="Lead received is Day 0 of the cadence."
              >
                <MenuItem value="lead_received">Lead received</MenuItem>
                <MenuItem value="lead_status_changed">Lead status changed</MenuItem>
              </TextField>

              {form.triggerEvent === 'lead_status_changed' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    select label="From status (optional)" value={form.fromStatus ?? ''}
                    onChange={(e) => set('fromStatus', e.target.value || null)}
                    sx={{ minWidth: 260, flex: 1 }}
                    helperText="Leave blank for any previous status"
                  >
                    <MenuItem value="">Any</MenuItem>
                    {selectable.map((s) => (
                      <MenuItem key={s.status} value={s.status}>{s.status}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select label="To status" value={form.toStatus ?? ''}
                    onChange={(e) => set('toStatus', e.target.value)}
                    sx={{ minWidth: 260, flex: 1 }}
                  >
                    {selectable.map((s) => (
                      <MenuItem key={s.status} value={s.status}>{s.status}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
            </>
          )}

          <Divider />

          <TextField
            label="Subject" value={form.subject ?? ''} onChange={(e) => set('subject', e.target.value)}
            inputProps={{ maxLength: 200 }} fullWidth
          />
          <TextField
            label="Inbox preview line (optional)" value={form.preheader ?? ''}
            onChange={(e) => set('preheader', e.target.value)}
            inputProps={{ maxLength: 200 }} fullWidth
            helperText="The grey line inboxes show beside the subject. Left blank, the subject is used."
          />
          <TextField
            label="Message" value={form.body ?? ''} onChange={(e) => set('body', e.target.value)}
            multiline minRows={8} fullWidth
            helperText="Goes into the SendGrid template. Use {{First Name}} and {{Rep Name}} where the lead's name and the rep's name belong. A blank line starts a new paragraph."
          />
          {/* Blank INHERITS the app-wide template, which is the point - one place to change it for
              every entry. The placeholder and helper text say which template that is, because a
              blank box otherwise reads as "no template attached". */}
          <TextField
            label="SendGrid template" value={form.sendGridTemplateId ?? ''}
            onChange={(e) => set('sendGridTemplateId', e.target.value)}
            fullWidth
            placeholder={settings?.defaultTemplateId ?? ''}
            InputLabelProps={{ shrink: true }}
            helperText={settings?.defaultTemplateId
              ? `Using the standard Laurus template ${settings.defaultTemplateId}, shared by every entry. Type an id beginning with 'd-' only to override it for this one email.`
              : "A dynamic template id beginning with 'd-'."}
          />

          {/* Preview renders through the SAME code the send will use, so what is checked here is
              what goes out. The sample name is fixed - a preview must never pull a real lead. */}
          <Box>
            <Button
              variant="outlined" startIcon={<VisibilityIcon />} disabled={previewing || !form.body}
              onClick={async () => {
                setPreviewing(true);
                setError(null);
                try {
                  setPreview(await previewDripEntry({
                    subject: form.subject, preheader: form.preheader, body: form.body,
                    sendGridTemplateId: form.sendGridTemplateId,
                  }));
                  setPreviewOpen(true);
                } catch (err) {
                  setError(dripErrorMessage(err, 'The preview could not be produced.'));
                } finally {
                  setPreviewing(false);
                }
              }}
            >
              {previewing ? 'Rendering…' : 'Preview the email'}
            </Button>
          </Box>

          {/* A real send, to a member of staff only. The server enforces the domain, the single
              recipient and the hourly cap; this is only the way in. */}
          <Box sx={{ p: 2, border: '1px solid var(--card-border)', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Send a test to staff</Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1.5 }}>
              Sends this email for real, through SendGrid, exactly as a lead would receive it —
              including the unsubscribe link. <strong>@lauruscollege.edu addresses only</strong>, one
              at a time. No lead is contacted and nothing is recorded against a lead.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                label="Staff address" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                size="small" sx={{ flex: '1 1 280px' }} autoComplete="off"
              />
              <Button
                variant="outlined" startIcon={<SendIcon />}
                disabled={testing || !form.body || !testTo}
                onClick={async () => {
                  setTesting(true);
                  setTestResult(null);
                  setError(null);
                  try {
                    const r = await sendDripTest({
                      name: form.name, subject: form.subject, preheader: form.preheader,
                      body: form.body, sendGridTemplateId: form.sendGridTemplateId,
                    }, testTo);
                    setTestResult(`Sent to ${r.to}. SendGrid accepted it (${r.statusCode}). `
                      + `${r.sentInLastHour} of ${r.limitPerHour} test sends used this hour.`);
                  } catch (err) {
                    setError(dripErrorMessage(err, 'The test could not be sent.'));
                  } finally {
                    setTesting(false);
                  }
                }}
                sx={{ mt: 0.25 }}
              >
                {testing ? 'Sending…' : 'Send test'}
              </Button>
            </Box>
            {testResult && <Alert severity="success" sx={{ mt: 1.5 }}>{testResult}</Alert>}
          </Box>

          <Alert severity="info">
            Saved as a <strong>draft</strong>, so this entry emails no lead. The seven EAB emails are
            switched on together from <em>Verity status checks</em> on the Drip Emails view, and every
            send is checked against Verity first. A test to staff, above, sends for real.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}
                sx={{ backgroundColor: 'var(--accent)' }}>
          {saving ? 'Saving…' : entry ? 'Save changes' : 'Save draft'}
        </Button>
      </DialogActions>

      {/* The preview, as its own window over the form. The email renders at the width a recipient
          sees, inside the crest-and-footer frame the SendGrid template provides, so the spacing
          being judged is the spacing that gets sent. */}
      <Dialog open={previewOpen && !!preview} onClose={() => setPreviewOpen(false)}
              maxWidth="md" fullWidth
              PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <VisibilityIcon fontSize="small" />
          Email preview
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setPreviewOpen(false)} aria-label="Close the preview"
                      sx={{ color: 'var(--text-secondary)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, backgroundColor: '#e9edf3' }}>
          {preview && (
            <>
              <Box sx={{ p: 2, backgroundColor: 'var(--card-bg)' }}>
                {preview.unresolvedTokens.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    These merge fields were not recognised and will go out exactly as written:{' '}
                    <strong>{preview.unresolvedTokens.map((t) => `{{${t}}}`).join(', ')}</strong>.
                    Only {'{{First Name}}'} and {'{{Rep Name}}'} are filled in.
                  </Alert>
                )}
                {(preview.blockers ?? []).length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {preview.blockers!.join(' ')} The wording is fine to save as a draft.
                  </Alert>
                )}
                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                  <strong>Subject:</strong> {preview.templateData.subject}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  Inbox preview line: {preview.templateData.preheader}<br />
                  Template: {preview.templateId ?? 'none configured'} · unsubscribe group:{' '}
                  {preview.unsubscribeGroupId ?? 'none configured'} · sample name:{' '}
                  {preview.sampleFirstName}
                  {preview.signatureFallback
                    ? ` · unassigned leads sign as: ${preview.signatureFallback}`
                    : ''}
                </Typography>
              </Box>
              <Box
                component="iframe"
                title="Email preview"
                // Sandboxed with srcDoc: the preview cannot run scripts or reach back into the app.
                sandbox=""
                srcDoc={`<!doctype html><html><body style="margin:0;padding:18px 0 28px;background:#e9edf3;font-family:Arial,Helvetica,sans-serif;">
<div style="max-width:620px;margin:0 auto;background:#ffffff;">
  <div style="background:#16233E;padding:20px;text-align:center;color:#ffffff;font-size:12px;letter-spacing:.06em;">LAURUS COLLEGE</div>
  <div style="height:4px;background:#f0a500;"></div>
  <div style="padding:24px 26px;font-size:15px;line-height:1.6;color:#1f2933;">${preview.templateData.body}</div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;line-height:1.7;color:#5a6880;background:#ffffff;">
    Laurus College &middot; Enrollment Operations<br>postal address<br>
    <span style="color:#B07A00;text-decoration:underline;font-weight:bold;">Unsubscribe from these emails</span>
  </div>
  <div style="height:4px;background:#f0a500;"></div>
  <div style="background:#002A50;padding:14px;text-align:center;color:#ffffff;font-size:11px;">Copyright &copy; Laurus College &middot; social links</div>
</div></body></html>`}
                sx={{ width: '100%', height: 'calc(92vh - 210px)', border: 0, display: 'block' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mr: 'auto', pl: 1 }}>
            The crest, footer and unsubscribe link come from the SendGrid template — shown here in
            outline so the spacing can be judged in context.
          </Typography>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
